import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { useField } from 'formik'
import { Form, Input } from 'antd'
import type { InputRef, InputProps } from 'antd'
import { applyMask, removeMask } from '../lib/mask'

interface FormikInputProps extends Omit<InputProps, 'name' | 'onBlur'> {
  name: string
  label: string
  formItemClassName?: string
  extra?: React.ReactNode
  mask?: string
  onBlur?: () => void | Promise<void>
  onChangeValue?: (value: string) => void
}

export const FormikInput = forwardRef<InputRef, FormikInputProps>(function FormikInput(
  { name, label, type, formItemClassName, extra, mask, onBlur, onChangeValue, ...inputProps },
  ref
) {
  const [field, meta] = useField(name)
  const inputRef = useRef<InputRef>(null)
  const hasError = meta.touched && !!meta.error

  useImperativeHandle(ref, () => inputRef.current!)

  useEffect(() => {
    if (inputRef.current?.input && mask) {
      const inputElement = inputRef.current.input
      applyMask(inputElement, mask)
      return () => removeMask(inputElement)
    }
  }, [mask])

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    field.onBlur(e) // marks field as touched so errors appear
    onBlur?.()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    field.onChange(e)
    onChangeValue?.(e.target.value)
  }

  return (
    <Form.Item
      label={label}
      validateStatus={hasError ? 'error' : ''}
      help={hasError ? meta.error : undefined}
      className={formItemClassName}
      extra={extra}
    >
      {type === 'password' ? (
        <Input.Password {...field} onBlur={handleBlur} {...inputProps} onChange={handleChange} />
      ) : (
        <Input ref={inputRef} {...field} onBlur={handleBlur} {...inputProps} onChange={handleChange} />
      )}
    </Form.Item>
  )
})
